import { useEffect, useState, useCallback } from "react";
import { apiFetch } from "@/api";

const APPLE_JS = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

function loadAppleScript() {
  return new Promise((resolve, reject) => {
    if (window.AppleID) return resolve();
    const existing = document.getElementById("apple-auth-js");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("apple-js")));
      return;
    }
    const s = document.createElement("script");
    s.id = "apple-auth-js";
    s.src = APPLE_JS;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("apple-js"));
    document.head.appendChild(s);
  });
}

// Self-contained "Sign in with Apple" button. Renders nothing until the backend
// reports Apple is configured. On success it hands a verified credential
// ({ identity_token, nonce, name }) to the parent, which calls auth.appleSignIn.
export default function AppleSignIn({ onCredential, onError, label = "Continue with Apple" }) {
  const [clientId, setClientId] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    apiFetch("/auth/apple/config", { auth: false })
      .then((cfg) => { if (alive && cfg?.configured && cfg?.web_client_id) setClientId(cfg.web_client_id); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!clientId) return;
    let alive = true;
    loadAppleScript().then(() => { if (alive) setReady(true); }).catch(() => {});
    return () => { alive = false; };
  }, [clientId]);

  const handleClick = useCallback(async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      const { nonce } = await apiFetch("/auth/apple/nonce", { auth: false });
      window.AppleID.auth.init({
        clientId,
        scope: "name email",
        redirectURI: `${window.location.origin}/auth/apple/callback`,
        usePopup: true,
        nonce,
      });
      const resp = await window.AppleID.auth.signIn();
      const idToken = resp?.authorization?.id_token;
      if (!idToken) throw new Error("Apple sign-in was cancelled.");
      const u = resp?.user?.name;
      const name = u ? { givenName: u.firstName, familyName: u.lastName } : undefined;
      await onCredential({ identity_token: idToken, nonce, name });
    } catch (e) {
      const code = e?.error || "";
      if (code !== "popup_closed_by_user" && code !== "user_cancelled_authorize") {
        onError?.(e?.message || "Apple sign-in failed. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }, [ready, busy, clientId, onCredential, onError]);

  if (!clientId) return null;

  return (
    <button
      type="button"
      data-testid="apple-signin-btn"
      onClick={handleClick}
      disabled={!ready || busy}
      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-black px-6 py-3 text-[15px] font-semibold text-white transition-opacity duration-200 hover:opacity-80 disabled:opacity-40"
    >
      <svg viewBox="0 0 384 512" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
      </svg>
      {label}
    </button>
  );
}
